import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";

const generateIdempotencyKey = (prefix = "inventory-transition") => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;
};

const MAX_CARDS_PER_REQUEST = 100;
const TERMINAL_WORKFLOW_STATUSES = [
  "COMPLETED",
  "FAILED",
  "CANCELED",
  "TERMINATED",
  "TIMED_OUT",
  "CONTINUED_AS_NEW",
];

const parseValues = (value) =>
  String(value ?? "")
    .split(/[\s,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const createPreviewKey = (mode, values, count) =>
  `${mode}|${values.join(" ")}|${String(count)}`;

export const INVENTORY_TRANSITION_CONFIGS = {
  reception: {
    title: "Receive card shipment",
    description:
      "Confirm physical receipt of cards before they can be assignment-enabled.",
    operationType: "SHIPMENT_RECEPTION",
    sourceStatus: "IN_TRANSIT",
    targetStatus: "RECEIVED",
    actionLabel: "Mark as received",
    operationLabel: "reception",
    endpoint: "/card-inventory/transitions",
    bundleEndpoint: "/card-inventory/transitions/bundles",
    previewEndpoint: "/card-inventory/transitions/preview",
    statusEndpoint: "/card-inventory/transitions",
  },
  assignEnablement: {
    title: "Enable card assignment",
    description:
      "Enable received cards for assignment without changing the transition workflow.",
    operationType: "ASSIGN_ENABLEMENT",
    sourceStatus: "RECEIVED",
    targetStatus: "ASSIGN_ENABLED",
    actionLabel: "Enable assignment",
    operationLabel: "assign enablement",
    endpoint: "/card-inventory/transitions",
    bundleEndpoint: "/card-inventory/transitions/bundles",
    previewEndpoint: "/card-inventory/transitions/preview",
    statusEndpoint: "/card-inventory/transitions",
  },
};

export default function CardInventoryTransition({
  transitionConfig = INVENTORY_TRANSITION_CONFIGS.reception,
}) {
  const config = transitionConfig;
  const [mode, setMode] = useState("CARD");
  const [activeTab, setActiveTab] = useState(0);
  const [selectionInput, setSelectionInput] = useState("");
  const [requestedReceivedCount, setRequestedReceivedCount] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [notes, setNotes] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState(
    generateIdempotencyKey(config.operationLabel)
  );
  const [acceptedOperation, setAcceptedOperation] = useState(null);
  const [receptionStatus, setReceptionStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewKey, setPreviewKey] = useState(null);
  const [errorFilter, setErrorFilter] = useState("ALL");
  const [gridPageSize, setGridPageSize] = useState(25);
  const [feedback, setFeedback] = useState({
    open: false,
    severity: "error",
    message: "",
  });
  const queryClient = useQueryClient();
  const { data: locations = [] } = useQuery(
    ["cardInventoryTransitionLocations"],
    async () =>
      (
        await HttpService.getAxiosClient().get(`${window.API_URL}/locations`)
      ).data
  );
  const {
    data: bundleOptions = [],
    isLoading: bundleQueryLoading,
    isFetching: bundleQueryFetching,
  } = useQuery(
    ["cardInventoryTransitionBundles", config.operationType, selectedLocationId],
    async () => {
      const params = new URLSearchParams({
        locationId: String(selectedLocationId),
        sourceStatus: config.sourceStatus,
      });
      const response = await HttpService.getAxiosClient().get(
        `${window.API_URL}${config.bundleEndpoint}?${params}`
      );
      return response.data || [];
    },
    {
      enabled: mode === "BUNDLE" && Boolean(selectedLocationId),
      onError: (error) => {
        setFeedback({
          open: true,
          severity: "error",
          message:
            error?.response?.data?.error ||
            "Unable to search available bundles.",
        });
      },
    }
  );
  const bundleSearchLoading =
    Boolean(selectedLocationId) &&
    (bundleQueryLoading || bundleQueryFetching);
  const parsedValues = useMemo(
    () => parseValues(selectionInput),
    [selectionInput]
  );
  const invalidValues = useMemo(
    () =>
      mode === "BUNDLE"
        ? parsedValues.filter((value) => !/^\d+$/.test(value))
        : parsedValues.filter((value) => !/^[a-z0-9_-]+$/i.test(value)),
    [mode, parsedValues]
  );
  const duplicateValues = useMemo(() => {
    const seen = new Set();
    return parsedValues.filter((value) => {
      const normalized = value.toLowerCase();
      if (seen.has(normalized)) return true;
      seen.add(normalized);
      return false;
    });
  }, [parsedValues]);
  const selectedIds = useMemo(
    () => parsedValues.map((value) => Number(value)),
    [parsedValues]
  );
  const currentPreviewKey = createPreviewKey(
    mode,
    mode === "BUNDLE" ? selectedIds : parsedValues,
    mode === "BUNDLE" ? requestedReceivedCount : parsedValues.length
  );
  const previewItems = previewResult?.items || [];
  const validPreviewCount = previewItems.filter((item) => item.valid).length;
  const invalidPreviewCount = previewItems.length - validPreviewCount;
  const errorOptions = useMemo(
    () => [
      "VALID",
      ...new Set(
        previewItems
          .filter((item) => !item.valid && item.errorCode)
          .map((item) => item.errorCode)
      ),
    ],
    [previewItems]
  );
  const filteredPreviewItems = useMemo(
    () =>
      errorFilter === "ALL"
        ? previewItems
        : errorFilter === "VALID"
        ? previewItems.filter((item) => item.valid)
        : previewItems.filter((item) => item.errorCode === errorFilter),
    [errorFilter, previewItems]
  );
  const previewColumns = useMemo(
    () => [
      { field: "proxy", headerName: "Card proxy", minWidth: 220, flex: 1 },
      {
        field: "valid",
        headerName: "Result",
        width: 110,
        valueGetter: (params) => (params.row.valid ? "Valid" : "Invalid"),
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {params.row.valid ? (
              <CheckCircleOutlineIcon color="success" fontSize="small" />
            ) : (
              <ErrorOutlineIcon color="error" fontSize="small" />
            )}
            <span>{params.value}</span>
          </Stack>
        ),
      },
      { field: "status", headerName: "Status", width: 140 },
      { field: "errorCode", headerName: "Error", minWidth: 190, flex: 1 },
      {
        field: "errorMessage",
        headerName: "Details",
        minWidth: 280,
        flex: 2,
      },
    ],
    []
  );

  const previewMutation = useMutation(
    (payload) =>
      HttpService.getAxiosClient().post(
        `${window.API_URL}${config.previewEndpoint}`,
        payload,
        { headers: { "Content-Type": "application/json" }, responseType: "json" }
      ),
    {
      onSuccess: ({ data }, variables) => {
        setPreviewResult(data);
        setPreviewKey(
          createPreviewKey(
            variables.mode,
            variables.mode === "BUNDLE"
              ? variables.bundleIds
              : variables.cardProxies,
            variables.requestedReceivedCount
          )
        );
        setErrorFilter("ALL");
        setActiveTab(1);
        setFeedback({
          open: true,
          severity: data.valid ? "success" : "error",
          message: data.valid
            ? "Validation passed. The shipment is ready to receive."
            : data.errorMessage || "Validation failed. Review the card results.",
        });
      },
      onError: (error) => {
        setPreviewResult(null);
        setPreviewKey(null);
        setErrorFilter("ALL");
        setFeedback({
          open: true,
          severity: "error",
          message:
            error?.response?.data?.error ||
            error.message ||
            `The ${config.operationLabel} could not be validated.`,
        });
      },
    }
  );

  const receptionMutation = useMutation(
    (payload) =>
      HttpService.getAxiosClient().post(
        `${window.API_URL}${config.endpoint}`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
          responseType: "json",
        }
      ),
    {
      onSuccess: ({ data }) => {
        setAcceptedOperation(data);
        setReceptionStatus({
          workflowId: data.workflowId,
          runId: data.runId,
          status: "RUNNING",
          result: null,
        });
        setStatusError(null);
        setActiveTab(2);
        setSelectionInput("");
        setRequestedReceivedCount("");
        setNotes("");
        setIdempotencyKey(generateIdempotencyKey(config.operationLabel));
        setFeedback({
          open: true,
          severity: "success",
          message:
            `${config.actionLabel} was submitted and is being processed.`,
        });
      },
      onError: (error) => {
        const responseData = error?.response?.data;
        const message =
          responseData?.error ||
          responseData?.message ||
          (typeof responseData === "string" ? responseData : null) ||
          error.message ||
          `The ${config.operationLabel} could not be submitted.`;
        setFeedback({ open: true, severity: "error", message });
      },
    }
  );

  useEffect(() => {
    if (!acceptedOperation?.workflowId) return undefined;

    let cancelled = false;
    const loadStatus = async () => {
      try {
        const { data } = await HttpService.getAxiosClient().get(
          `${window.API_URL}${config.statusEndpoint}/${encodeURIComponent(
            acceptedOperation.workflowId
          )}/status`,
          { responseType: "json" }
        );
        if (!cancelled) {
          setReceptionStatus(data);
          setStatusError(null);
        }
      } catch (error) {
        if (!cancelled) {
          setStatusError(
            error?.response?.data?.error ||
              error.message ||
              `The ${config.operationLabel} status could not be loaded.`
          );
        }
      }
    };

    loadStatus();
    const intervalId = window.setInterval(() => {
      if (!TERMINAL_WORKFLOW_STATUSES.includes(receptionStatus?.status)) {
        loadStatus();
      }
    }, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [acceptedOperation, receptionStatus?.status]);

  useEffect(() => {
    if (
      !acceptedOperation ||
      !TERMINAL_WORKFLOW_STATUSES.includes(receptionStatus?.status)
    ) {
      return;
    }

    queryClient.removeQueries([
      "cardInventoryTransitionBundles",
      config.operationType,
    ]);
    setSelectedLocationId("");
    setPreviewResult(null);
    setPreviewKey(null);
    setErrorFilter("ALL");
  }, [
    acceptedOperation,
    queryClient,
    receptionStatus?.status,
  ]);

  const hasInvalidSelection =
    parsedValues.length === 0 ||
    invalidValues.length > 0 ||
    duplicateValues.length > 0;
  const hasTooManyCards =
    mode === "CARD" && parsedValues.length > MAX_CARDS_PER_REQUEST;
  const parsedCount = Number(requestedReceivedCount);
  const hasInvalidCount =
    mode === "BUNDLE" &&
    (!Number.isInteger(parsedCount) || parsedCount <= 0);
  const canSubmit =
    !hasInvalidSelection &&
    !hasTooManyCards &&
    !hasInvalidCount &&
    previewKey === currentPreviewKey &&
    previewResult?.valid === true &&
    !receptionMutation.isLoading;

  const previewReception = (selection = selectionInput, count = parsedCount) => {
    const values = parseValues(selection);
    const ids = values.map((value) => Number(value));
    const invalidSelection =
      values.length === 0 ||
      values.some((value) =>
        mode === "BUNDLE" ? !/^\d+$/.test(value) : !/^[a-z0-9_-]+$/i.test(value)
      ) ||
      new Set(values.map((value) => value.toLowerCase())).size !== values.length;
    const invalidCount =
      mode === "BUNDLE" && (!Number.isInteger(count) || count <= 0);
    if (
      invalidSelection ||
      (mode === "CARD" && values.length > MAX_CARDS_PER_REQUEST) ||
      invalidCount ||
      previewMutation.isLoading
    )
      return;
    previewMutation.mutate({
      mode,
      bundleIds: mode === "BUNDLE" ? ids : [],
      cardProxies: mode === "CARD" ? values : [],
      requestedReceivedCount: mode === "BUNDLE" ? count : values.length,
      sourceStatus: config.sourceStatus,
      targetStatus: config.targetStatus,
      operationType: config.operationType,
    });
  };

  const submitReception = (event) => {
    event?.preventDefault();
    if (!canSubmit) return;

    receptionMutation.mutate({
      mode,
      bundleIds: mode === "BUNDLE" ? selectedIds : [],
      cardProxies: mode === "CARD" ? parsedValues : [],
      requestedReceivedCount:
        mode === "BUNDLE" ? parsedCount : selectedIds.length,
      sourceStatus: config.sourceStatus,
      targetStatus: config.targetStatus,
      operationType: config.operationType,
      notes: notes.trim() || null,
      idempotencyKey,
    });
  };

  const clearForm = () => {
    setSelectionInput("");
    setRequestedReceivedCount("");
    setNotes("");
    setPreviewResult(null);
    setPreviewKey(null);
    setAcceptedOperation(null);
    setReceptionStatus(null);
    setStatusError(null);
    setActiveTab(0);
    setIdempotencyKey(generateIdempotencyKey(config.operationLabel));
    setSelectedLocationId("");
    queryClient.removeQueries([
      "cardInventoryTransitionBundles",
      config.operationType,
    ]);
  };

  const selectBundle = (bundle) => {
    const bundleId = String(bundle.id);
    const totalCards = Number(bundle.totalCards);
    setSelectionInput(bundleId);
    setRequestedReceivedCount(String(totalCards));
    setPreviewResult(null);
    setPreviewKey(null);
    setErrorFilter("ALL");
    previewReception(bundleId, totalCards);
  };

  const copyPreviewProxies = async () => {
    const proxies = filteredPreviewItems
      .map((item) => item.proxy)
      .filter(Boolean)
      .join("\n");
    if (!proxies) {
      setFeedback({
        open: true,
        severity: "error",
        message: "There are no card proxies to copy.",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(proxies);
      setFeedback({
        open: true,
        severity: "success",
        message: `${filteredPreviewItems.length} card proxies copied to the clipboard.`,
      });
    } catch (error) {
      console.error("Failed to copy card proxies:", error);
      setFeedback({
        open: true,
        severity: "error",
        message: "Unable to copy card proxies to the clipboard.",
      });
    }
  };

  const selectionLabel = mode === "BUNDLE" ? "Bundle IDs" : "Card proxies";
  const selectionPlaceholder =
    mode === "BUNDLE" ? "2001\n2002" : "12345\n67890";

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {config.title}
        </Typography>
        <Typography color="text.secondary">
          {config.description} The entire {config.operationLabel} batch
          succeeds or is rejected together.
        </Typography>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        sx={{ mb: 2 }}
      >
        <Tab label="1. Validate" />
        <Tab
          label={`2. ${config.actionLabel}${
            previewItems.length ? ` (${previewItems.length})` : ""
          }`}
          disabled={previewItems.length === 0}
        />
        <Tab label="3. Result" disabled={!acceptedOperation} />
      </Tabs>

      <Paper
        component="form"
        onSubmit={submitReception}
        elevation={2}
        sx={{ p: 3, display: activeTab === 0 ? "block" : "none" }}
      >
        <Stack spacing={3}>
          <FormControl fullWidth>
            <InputLabel id="reception-mode-label">Transition mode</InputLabel>
            <Select
              labelId="reception-mode-label"
              value={mode}
              label="Transition mode"
              onChange={(event) => {
                setMode(event.target.value);
                setSelectionInput("");
                setRequestedReceivedCount("");
                setPreviewResult(null);
                setPreviewKey(null);
              }}
            >
              <MenuItem value="CARD">Individual cards</MenuItem>
              <MenuItem value="BUNDLE">Complete bundles</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label={selectionLabel}
            value={selectionInput}
            onChange={(event) => setSelectionInput(event.target.value)}
            placeholder={selectionPlaceholder}
            multiline
            minRows={5}
            fullWidth
            required
            sx={mode === "BUNDLE" ? { display: "none" } : undefined}
            error={
              hasTooManyCards ||
              invalidValues.length > 0 ||
              duplicateValues.length > 0
            }
            helperText={
              hasTooManyCards
                ? `A maximum of ${MAX_CARDS_PER_REQUEST} cards can be received per request.`
                : invalidValues.length > 0
                ? `Invalid value(s): ${invalidValues.join(", ")}`
                : duplicateValues.length > 0
                ? `Duplicate value(s): ${duplicateValues.join(", ")}`
                : "Separate proxies with spaces, commas, semicolons, or new lines."
            }
          />

          {mode === "BUNDLE" && (
            <>
              <FormControl fullWidth required>
                <InputLabel id="reception-location-label">
                  Shipment location
                </InputLabel>
                <Select
                  labelId="reception-location-label"
                  value={selectedLocationId}
                  label="Shipment location"
                  onChange={(event) => {
                    const locationId = event.target.value;
                    setSelectedLocationId(locationId);
                    setSelectionInput("");
                    setRequestedReceivedCount("");
                    setPreviewResult(null);
                    setPreviewKey(null);
                  }}
                >
                  {locations.map((location) => (
                    <MenuItem
                      key={location.id}
                      value={location.id}
                    >
                      {location.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
              Select a bundle with at least one card currently in{" "}
              {config.sourceStatus}.
                Selected bundle:{" "}
                {selectionInput ? `${selectionInput} (${requestedReceivedCount} cards)` : "none"}
              </Typography>
              {bundleSearchLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box sx={{ height: 360, width: "100%" }}>
                  <DataGrid
                    rows={bundleOptions}
                    getRowId={(row) => row.id}
                    columns={[
                      { field: "description", headerName: "Description", flex: 1, minWidth: 180 },
                      { field: "locationName", headerName: "Location", width: 160 },
                      { field: "totalCards", headerName: "Total cards", width: 110 },
                      {
                        field: "inTransitCards",
                        headerName: config.sourceStatus,
                        width: 120,
                      },
                      { field: "shippingMethod", headerName: "Shipping method", width: 140 },
                      { field: "trackingNumber", headerName: "Tracking number", width: 150 },
                      {
                        field: "createdAt",
                        headerName: "Created",
                        width: 130,
                        valueGetter: ({ value }) =>
                          value ? new Date(value).toLocaleDateString() : "",
                      },
                      {
                        field: "select",
                        headerName: "",
                        width: 90,
                        sortable: false,
                        renderCell: ({ row }) => (
                          <Button size="small" onClick={() => selectBundle(row)}>
                            Select
                          </Button>
                        ),
                      },
                    ]}
                    disableSelectionOnClick
                    localeText={{ noRowsLabel: "No eligible bundles found." }}
                  />
                </Box>
              )}
            </>
          )}

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`${parsedValues.length} ${
                mode === "BUNDLE" ? "bundle(s)" : "card(s)"
              } ready`}
              color={canSubmit ? "primary" : "default"}
              variant={canSubmit ? "filled" : "outlined"}
            />
            <Typography variant="body2" color="text.secondary">
              Only cards currently in {config.sourceStatus} can transition to{" "}
              {config.targetStatus}.
            </Typography>
          </Stack>

          <Box>
            <Button
              type="button"
              variant="outlined"
              onClick={() => previewReception()}
              disabled={
                hasInvalidSelection ||
                hasTooManyCards ||
                hasInvalidCount ||
                previewMutation.isLoading
              }
              sx={{ mr: 1 }}
              startIcon={
                previewMutation.isLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : null
              }
            >
              {previewMutation.isLoading ? "Validating..." : "Preview / validate"}
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={clearForm}
              disabled={
                previewMutation.isLoading || receptionMutation.isLoading
              }
            >
              Clear
            </Button>
          </Box>
        </Stack>
      </Paper>


      {previewItems.length > 0 && (
        <Paper
          elevation={2}
          sx={{ p: 3, display: activeTab === 1 ? "block" : "none" }}
        >
          <Stack spacing={2}>
            <Alert severity={previewResult?.valid ? "success" : "error"}>
              <strong>
                {previewResult?.valid
                  ? "Valid — ready to continue"
                  : "Invalid — return to Validate and correct the selection"}
              </strong>
            </Alert>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ sm: "center" }}
            >
              <Typography variant="subtitle1">
                Validation results ({validPreviewCount} ready,{" "}
                {invalidPreviewCount} rejected)
              </Typography>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel id="reception-error-filter-label">
                  Filter results
                </InputLabel>
                <Select
                  labelId="reception-error-filter-label"
                  value={errorFilter}
                  label="Filter results"
                  onChange={(event) => setErrorFilter(event.target.value)}
                >
                  <MenuItem value="ALL">All results</MenuItem>
                  {errorOptions.map((errorCode) => (
                    <MenuItem key={errorCode} value={errorCode}>
                      {errorCode}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                type="button"
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={copyPreviewProxies}
                disabled={filteredPreviewItems.length === 0}
              >
                Copy proxies
              </Button>
            </Stack>
            <Box sx={{ width: "100%" }}>
              <DataGrid
                autoHeight
                rows={filteredPreviewItems.map((item, index) => ({
                  id: `${item.proxy}-${index}`,
                  ...item,
                }))}
                columns={previewColumns}
                pagination
                pageSize={gridPageSize}
                onPageSizeChange={(newPageSize) => setGridPageSize(newPageSize)}
                rowsPerPageOptions={[25, 50, 100]}
                components={{ Toolbar: GridToolbar }}
                disableSelectionOnClick
                density="compact"
                getRowClassName={(params) =>
                  params.row.valid
                    ? "reception-valid-row"
                    : "reception-invalid-row"
                }
              />
            </Box>
            <TextField
              label="Notes (optional)"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              multiline
              minRows={2}
              fullWidth
              disabled={!previewResult?.valid}
              inputProps={{ maxLength: 2000 }}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {previewResult?.valid ? (
                <Button
                  type="button"
                  variant="contained"
                  onClick={() => submitReception()}
                  disabled={!canSubmit}
                  startIcon={
                    receptionMutation.isLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : null
                  }
                >
                  {receptionMutation.isLoading
                    ? "Submitting..."
                    : config.actionLabel}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => setActiveTab(0)}
                >
                  Back
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>
      )}

      {acceptedOperation && activeTab === 2 && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Stack spacing={2}>
            {statusError && <Alert severity="error">{statusError}</Alert>}
            {!statusError &&
              receptionStatus?.status === "COMPLETED" && (
              <Alert
                severity={
                  receptionStatus.result?.result === "APPLIED"
                    ? "success"
                    : "error"
                }
              >
                <strong>
                  {receptionStatus.result?.result === "APPLIED"
                    ? `${config.actionLabel} completed successfully`
                    : `${config.actionLabel} was rejected`}
                </strong>
              </Alert>
            )}
            {!statusError &&
              receptionStatus?.status &&
              !TERMINAL_WORKFLOW_STATUSES.includes(receptionStatus.status) && (
              <Alert severity="info">
                <CircularProgress size={18} sx={{ mr: 1, verticalAlign: "middle" }} />
                {config.actionLabel} is processing. The result will update
                automatically.
              </Alert>
            )}
            {!statusError &&
              receptionStatus?.status &&
              TERMINAL_WORKFLOW_STATUSES.includes(receptionStatus.status) &&
              receptionStatus.status !== "COMPLETED" && (
                <Alert severity="error">
                  {config.actionLabel} workflow ended with status{" "}
                  <strong>{receptionStatus.status}</strong>.
                </Alert>
              )}
            {receptionStatus?.result && (
              <Stack spacing={1}>
                <Typography>
                  Status: <strong>{receptionStatus.status}</strong>
                </Typography>
                <Typography>
                  Result: <strong>{receptionStatus.result.result}</strong>
                </Typography>
                {receptionStatus.result.transitionedCount != null && (
                  <Typography>
                    Cards transitioned:{" "}
                    <strong>{receptionStatus.result.transitionedCount}</strong>
                  </Typography>
                )}
                {receptionStatus.result.operationId && (
                  <Typography>
                    Operation ID:{" "}
                    <strong>{receptionStatus.result.operationId}</strong>
                  </Typography>
                )}
                {receptionStatus.result.errorCode && (
                  <Typography color="error">
                    {receptionStatus.result.errorCode}:{" "}
                    {receptionStatus.result.errorMessage}
                  </Typography>
                )}
              </Stack>
            )}
            <Typography variant="body2" color="text.secondary">
              Workflow ID: {acceptedOperation.workflowId}
              {acceptedOperation.runId ? ` · Run ID: ${acceptedOperation.runId}` : ""}
            </Typography>
            <Button variant="outlined" onClick={() => setActiveTab(0)}>
              Start another {config.operationLabel}
            </Button>
          </Stack>
        </Paper>
      )}

      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setFeedback((current) => ({ ...current, open: false }))}
      >
        <Alert
          severity={feedback.severity}
          variant="filled"
          onClose={() => setFeedback((current) => ({ ...current, open: false }))}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
